/** Small display helpers shared across the UI. Pure; no engine coupling. */

export function pct(value: unknown, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return `${Number(value).toFixed(digits)}%`;
}

export function score(value: unknown, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return Number(value).toFixed(digits);
}

/** "HH:MM:SS" → "HH:MM" for compact display. */
export function shortTime(t: string | null | undefined): string {
  if (!t) return "—";
  // A multi-day candidate key carries an explicit day ("2024-01-02T05:30:00"); show only the clock.
  const clock = String(t).includes("T") ? String(t).split("T").pop()! : String(t);
  const parts = clock.split(":");
  return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : clock;
}

/**
 * Full seconds-precise clock ("HH:MM:SS"), date prefix stripped. For the surfaces where seconds
 * are the whole point — marked-time comparison, the final pick, second-level drill-downs — where
 * shortTime's HH:MM would collapse two candidates seconds apart into the same label.
 */
export function fullClock(t: string | null | undefined): string {
  if (!t) return "—";
  const clock = String(t).includes("T") ? String(t).split("T").pop()! : String(t);
  const parts = clock.split(":");
  if (parts.length >= 3) return `${parts[0]}:${parts[1]}:${parts[2]}`;
  if (parts.length === 2) return `${parts[0]}:${parts[1]}:00`;
  return clock;
}

/**
 * A USD amount for the AI-spend readouts. Small sums are the norm here (a chat turn is fractions
 * of a cent), so the precision grows as the number shrinks rather than rounding real spend to
 * "$0.00"; anything non-zero below the finest step shows as "<$0.0001".
 */
export function usd(value: unknown): string {
  const n = Number(value);
  if (value === null || value === undefined || Number.isNaN(n)) return "—";
  if (n === 0) return "$0.00";
  if (Math.abs(n) < 0.0001) return "<$0.0001";
  if (Math.abs(n) < 0.01) return `$${n.toFixed(4)}`;
  if (Math.abs(n) < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

/** 1234 → "1,234" (token counts). */
export function tokens(value: unknown): string {
  const n = Number(value);
  if (value === null || value === undefined || Number.isNaN(n)) return "—";
  return Math.trunc(n).toLocaleString("en-US");
}

export function titleCase(s: string): string {
  return s.replace(/(^|[\s_-])(\w)/g, (_, sep, ch) => (sep === "_" || sep === "-" ? " " : sep) + ch.toUpperCase());
}
